import type { ApiClientInterface, StorageInterface } from "../auth/types";

// Storage keys
export const ACCESS_TOKEN_KEY = "access_token";
export const REFRESH_TOKEN_KEY = "refresh_token";
export const TOKEN_EXPIRY_KEY = "token_expiry";
export const USER_DATA_KEY = "user_data";
export const AUTH_STATE_KEY = "auth_state";

// Base API URL
let baseApiUrl = "";
// Storage interface for cross-platform compatibility
let storageInterface: StorageInterface | null = null;

// Function to initialize the API client with the base URL and storage
export const initializeApiClient = (
  apiUrl: string,
  storage: StorageInterface,
) => {
  console.log("apiUrl FROM PARENT", apiUrl);
  baseApiUrl = apiUrl;
  storageInterface = storage;
  // Add a property to the apiClient for checking initialization
  // Object.defineProperty(apiClient, '_isInitialized', {
  //   get: () => isInitialized
  // });
};

// Variables for token refresh throttling
let isRefreshing = false;
let refreshQueue: Array<(token: string) => void> = [];
let lastRefreshAttempt = 0;
const REFRESH_THROTTLE_MS = 5000; // 5 seconds throttle

// Process all queued requests with the new token
const processQueue = (token: string): void => {
  refreshQueue.forEach((resolve) => resolve(token));
  refreshQueue = [];
};

// Get the stored access token
const getAccessToken = async (): Promise<string | null> => {
  try {
    if (!storageInterface) {
      console.error("Storage interface not initialized");
      return null;
    }
    return await storageInterface.getItem(ACCESS_TOKEN_KEY);
  } catch (e) {
    console.error("Error getting access token:", e);
    return null;
  }
};

// Get the stored refresh token
const getRefreshToken = async (): Promise<string | null> => {
  try {
    if (!storageInterface) {
      console.error("Storage interface not initialized");
      return null;
    }
    return await storageInterface.getItem(REFRESH_TOKEN_KEY);
  } catch (e) {
    console.error("Error getting refresh token:", e);
    return null;
  }
};

// Get the token expiry timestamp
const getTokenExpiry = async (): Promise<number | null> => {
  try {
    if (!storageInterface) {
      console.error("Storage interface not initialized");
      return null;
    }
    const expiry = await storageInterface.getItem(TOKEN_EXPIRY_KEY);
    return expiry ? parseInt(expiry, 10) : null;
  } catch (e) {
    console.error("Error getting token expiry:", e);
    return null;
  }
};

// Check if token is expired or about to expire (within 30 seconds)
const isTokenExpired = async (): Promise<boolean> => {
  const expiry = await getTokenExpiry();
  if (!expiry) return true;

  // Token is considered expired if it's within 30 seconds of expiry
  return Date.now() > expiry - 30000;
};

// API client for web and mobile
export const apiClient: ApiClientInterface = {
  get: async <T>(endpoint: string, options?: RequestInit): Promise<T> => {
    // Check if token is expired and refresh if needed
    if (await isTokenExpired()) {
      await apiClient.refreshTokenIfNeeded();
    }

    // Get the current access token
    const token = await getAccessToken();

    // Construct full URL
    const fullUrl = `${baseApiUrl}${endpoint}`;

    const response = await fetch(fullUrl, {
      ...options,
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(options?.headers || {}),
      },
    });

    // Handle 401 Unauthorized error (token might be invalid)
    if (response.status === 401) {
      // Try to refresh the token and retry the request
      const refreshed = await apiClient.refreshTokenIfNeeded();
      if (refreshed) {
        // Retry with new token
        return apiClient.get(endpoint, options);
      } else {
        throw new Error("Authentication failed");
      }
    }

    const responseData = await response.json();

    if (!response.ok) {
      const error = responseData;
      throw error;
    }

    return responseData;
  },

  post: async <T>(
    endpoint: string,
    data: any,
    options?: RequestInit,
  ): Promise<T> => {
    // Skip token check for auth endpoints
    const isAuthEndpoint =
      endpoint.includes("/auth/login") ||
      endpoint.includes("/auth/signup") ||
      endpoint.includes("/auth/forgot-password");

    // Check if token is expired and refresh if needed (except for auth endpoints)
    if (!isAuthEndpoint && (await isTokenExpired())) {
      await apiClient.refreshTokenIfNeeded();
    }

    // Get the current access token
    const token = await getAccessToken();

    // Construct full URL
    const fullUrl = `${baseApiUrl}${endpoint}`;

    const response = await fetch(fullUrl, {
      ...options,
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(token && !isAuthEndpoint
          ? { Authorization: `Bearer ${token}` }
          : {}),
        ...(options?.headers || {}),
      },
      body: JSON.stringify(data),
    });

    // Handle 401 Unauthorized error (token might be invalid)
    if (response.status === 401 && !isAuthEndpoint) {
      // Try to refresh the token and retry the request
      const refreshed = await apiClient.refreshTokenIfNeeded();
      if (refreshed) {
        // Retry with new token
        return apiClient.post(endpoint, data, options);
      } else {
        throw new Error("Authentication failed");
      }
    }

    const responseData = await response.json();

    if (!response.ok) {
      const error = responseData;
      throw error;
    }

    return responseData;
  },

  put: async <T>(
    endpoint: string,
    data: any,
    options?: RequestInit,
  ): Promise<T> => {
    // Check if token is expired and refresh if needed
    if (await isTokenExpired()) {
      await apiClient.refreshTokenIfNeeded();
    }

    // Get the current access token
    const token = await getAccessToken();

    // Construct full URL
    const fullUrl = `${baseApiUrl}${endpoint}`;

    const response = await fetch(fullUrl, {
      ...options,
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(options?.headers || {}),
      },
      body: JSON.stringify(data),
    });

    // Handle 401 Unauthorized error (token might be invalid)
    if (response.status === 401) {
      // Try to refresh the token and retry the request
      const refreshed = await apiClient.refreshTokenIfNeeded();
      if (refreshed) {
        // Retry with new token
        return apiClient.put(endpoint, data, options);
      } else {
        throw new Error("Authentication failed");
      }
    }

    const responseData = await response.json();

    if (!response.ok) {
      const error = responseData;
      throw error;
    }

    return responseData;
  },

  patch: async <T>(
    endpoint: string,
    data: any,
    options?: RequestInit,
  ): Promise<T> => {
    // Check if token is expired and refresh if needed
    if (await isTokenExpired()) {
      await apiClient.refreshTokenIfNeeded();
    }

    // Get the current access token
    const token = await getAccessToken();

    // Construct full URL
    const fullUrl = `${baseApiUrl}${endpoint}`;

    const response = await fetch(fullUrl, {
      ...options,
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(options?.headers || {}),
      },
      body: JSON.stringify(data),
    });

    // Handle 401 Unauthorized error (token might be invalid)
    if (response.status === 401) {
      // Try to refresh the token and retry the request
      const refreshed = await apiClient.refreshTokenIfNeeded();
      if (refreshed) {
        // Retry with new token
        return apiClient.patch(endpoint, data, options);
      } else {
        throw new Error("Authentication failed");
      }
    }

    const responseData = await response.json();

    if (!response.ok) {
      const error = responseData;
      throw error;
    }

    return responseData;
  },

  delete: async <T>(endpoint: string, options?: RequestInit): Promise<T> => {
    // Check if token is expired and refresh if needed
    if (await isTokenExpired()) {
      await apiClient.refreshTokenIfNeeded();
    }

    // Get the current access token
    const token = await getAccessToken();

    // Construct full URL
    const fullUrl = `${baseApiUrl}${endpoint}`;

    const response = await fetch(fullUrl, {
      ...options,
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(options?.headers || {}),
      },
    });

    // Handle 401 Unauthorized error (token might be invalid)
    if (response.status === 401) {
      // Try to refresh the token and retry the request
      const refreshed = await apiClient.refreshTokenIfNeeded();
      if (refreshed) {
        // Retry with new token
        return apiClient.delete(endpoint, options);
      } else {
        throw new Error("Authentication failed");
      }
    }

    const responseData = await response.json();

    if (!response.ok) {
      const error = responseData;
      throw error;
    }

    return responseData;
  },

  changePassword: async (
    oldPassword: string,
    newPassword: string,
  ): Promise<{ success: boolean; message: string }> => {
    // Check if token is expired and refresh if needed
    if (await isTokenExpired()) {
      await apiClient.refreshTokenIfNeeded();
    }

    // Get the current access token
    const token = await getAccessToken();

    // Construct full URL
    const fullUrl = `${baseApiUrl}/auth/change-password`;

    const response = await fetch(fullUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ oldPassword, newPassword }),
    });

    // Handle 401 Unauthorized error (token might be invalid)
    if (response.status === 401) {
      // Try to refresh the token and retry the request
      const refreshed = await apiClient.refreshTokenIfNeeded();
      if (refreshed) {
        // Retry with new token
        return apiClient.changePassword(oldPassword, newPassword);
      } else {
        throw new Error("Authentication failed");
      }
    }

    const responseData = await response.json();

    if (!response.ok) {
      const error = responseData;
      throw error;
    }

    return responseData;
  },

  // Helper method to refresh token if needed with throttling
  refreshTokenIfNeeded: async (): Promise<boolean> => {
    // Throttle refresh attempts
    const now = Date.now();
    if (now - lastRefreshAttempt < REFRESH_THROTTLE_MS) {
      console.log("Token refresh throttled, waiting for previous attempt");

      // If already refreshing, wait for it to complete
      if (isRefreshing) {
        return new Promise<boolean>((resolve) => {
          refreshQueue.push(() => resolve(true));
        });
      }

      // If not refreshing but throttled, return false to avoid excessive attempts
      return false;
    }

    // Update last attempt timestamp
    lastRefreshAttempt = now;

    // If already refreshing, wait for it to complete
    if (isRefreshing) {
      return new Promise<boolean>((resolve) => {
        refreshQueue.push(() => resolve(true));
      });
    }

    // Check if token is expired
    if (await isTokenExpired()) {
      isRefreshing = true;
      try {
        const refreshToken = await getRefreshToken();

        if (!refreshToken) {
          console.error("No refresh token available");
          isRefreshing = false;
          return false;
        }

        // Construct full URL
        const fullUrl = `${baseApiUrl}/auth/refresh`;

        const response = await fetch(fullUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ refreshToken }),
        });

        if (!response.ok) {
          throw new Error(`Failed to refresh token: ${response.status}`);
        }

        const data = await response.json();

        if (!storageInterface) {
          console.error("Storage interface not initialized");
          isRefreshing = false;
          return false;
        }

        // Update stored tokens
        const expiryTime = Date.now() + data.expiresIn * 1000;
        await storageInterface.setItem(ACCESS_TOKEN_KEY, data.accessToken);
        await storageInterface.setItem(REFRESH_TOKEN_KEY, data.refreshToken);
        await storageInterface.setItem(TOKEN_EXPIRY_KEY, expiryTime.toString());

        isRefreshing = false;
        processQueue(data.accessToken);
        return true;
      } catch (error) {
        console.error("Error refreshing token:", error);
        isRefreshing = false;

        // Clear the queue with errors
        refreshQueue.forEach((resolve) => resolve(""));
        refreshQueue = [];
        return false;
      }
    }

    return true;
  },
};
